// Self-contained bitmask check (same semantics as `checkPermissions.checkPermission`)
// inlined on purpose: `checkPermissions` imports `@app/router`, which drags the
// whole route graph into any test that touches it. Keeping this module
// dependency-free leaves `isRoutePermitted` unit-testable in isolation. Guards
// null/undefined so it never throws on `BigInt(undefined)`.
const grants = (accountPermissions, permission) => {
  if (accountPermissions == null) return false;
  const biAccount = window.BigInt(accountPermissions);
  const biPermission = window.BigInt(permission);
  return (biAccount & biPermission) === biPermission;
};

/**
 * Route-level permission gate used by ProtectedRoute.
 *
 * In a DS child-account context (the route pattern carries `/accounts/:accountUuid`,
 * i.e. a Distribution Sponsor is managing a child account), the permission that
 * authorises the route can live on EITHER the acting (current) account or the
 * child account being managed — allow if either grants it.
 *
 * This mirrors the per-page affordance rule (e.g. PatientDashboardPage's
 * `editPermissions = accountUuid ? childAccountPermissions : currentAccountPermissions`,
 * also used by DeletePatientMenuItem / OrdersTable). Using OR rather than swapping
 * to child-only is deliberate: it never removes access from DS routes gated on a
 * permission the DS holds but the child does not (e.g. uploads -> ADMIN_PATIENT_IMPORT,
 * claims -> CLAIMS_GET). It only ADDS the child-permission path that was missing,
 * which is why DS patient-edit (PATIENT_PATCH on the child, not the DS) was bouncing.
 *
 * Outside DS context only the current account's permissions apply (legacy 1:1).
 *
 * `checkPermission` throws on a null/undefined bitmask (BigInt(undefined)), so the
 * child branch is guarded — `childAccountPermissions` is absent for non-DS sessions.
 */
export const isRoutePermitted = ({
  requiredPermission,
  currentAccountPermissions,
  childAccountPermissions,
  hasChildContext,
}) => {
  if (!requiredPermission) return true;
  if (grants(currentAccountPermissions, requiredPermission)) return true;
  if (hasChildContext && grants(childAccountPermissions, requiredPermission)) return true;
  return false;
};
